<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import {
  Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot,
  RadioGroup, RadioGroupDescription, RadioGroupLabel, RadioGroupOption
} from '@headlessui/vue';

import { DocumentTextIcon, PaperClipIcon, ShieldCheckIcon, ChatBubbleBottomCenterTextIcon, CheckIcon } from '@heroicons/vue/24/outline';
import { ClaimTypeName } from '../../utils/config/constants/claims-types';
import { UploadcareFile } from '@/interfaces/uploadcare';
import FileInput from '@/components/files/FileInput.vue';

import adminAPI from '@/services/api';
import { ClaimOverview } from '@/pages/plugin/AgentFeedback.vue';
import AlertModal from '@/components/ui/modals/AlertModal.vue';
import { scrollToTop } from '../../utils/index';
import SetTravelAgencyCompany from '../overview/SetBusinessTravelCompany.vue';
import { useClaimStore } from '@/store';
import { useRoute } from 'vue-router';
import router from '@/router';
import ToggleWithDescription from '../ui/toggles/ToggleWithDescription.vue';
import Alert from '../ui/alerts/Alert.vue';
import { form as DefaultFormAdditionalLoss } from "./DefaultAdditionalLossForm";

const props = defineProps<{ claimOverview: ClaimOverview }>();
const emits = defineEmits(["nextStep"]);

interface Option {
  id: number;
  name: string;
  description: string;
  eligible: boolean | null;
}

// const settings: Option[] = options[props.claimOverview.claimType];
const settings: Option[] = [
  { id: 1, name: "Confirmer le dossier", description: "Vous pourrez nous communiquer les préjudices sur le formulaire suivant", eligible: true, },
  // { id: 4, name: "Je confirme · Avec préjudices", description: "Vous pourrez nous communiquer les préjudices sur le formulaire suivant", eligible: true, },
  { id: 5, name: "Je ferme le dossier", description: "Merci de nous indiquer la raison dans la note", eligible: false, }
]
const selected = ref(settings[0])

const tabs = [
  { name: 'Voyage d\'Affaires', b2c: false },
  { name: 'Voyage Loisir',  b2c: true },
]
const selectedTab = ref(props.claimOverview.isOfficeB2b || props.claimOverview.travelAgencyCompany
   ? tabs[0] : tabs[1]
);

const files = ref<UploadcareFile[]>([]);
const open = ref(false);
const openTerminationModal = ref(false);

const note = ref();
const additionalLoss = ref(false);

const isLoading = ref(false);
async function submitForm() {
  if (selected.value.eligible !== true) {
    open.value = true; // Open modal to confirm non-eligibility
    return;
  }
  isLoading.value = true;
  let stepToAdd = 1;
  try {
    const { success } = await saveAgentFeedback();
    if (success) {
      if (!additionalLoss.value && selectedTab.value.b2c) {
        stepToAdd++;
        await adminAPI.saveAgentFeedbackAdditionalLoss(props.claimOverview.claimId, DefaultFormAdditionalLoss.value);
        await useClaimStore().refreshClaim();
      }
    }
    emits("nextStep", stepToAdd);
  } catch (e) {
    console.error(e)
  } finally {
    isLoading.value = false;
  }
}

function saveAgentFeedback() {
  scrollToTop();
  return adminAPI.saveAgentFeedbackClaimValidation(props.claimOverview.claimId, {
    optionId: selected.value.id,
    note: note.value,
    receivableContract: undefined,
    b2c: selectedTab.value.b2c
  });
}

const customerNoteData = computed(() => useClaimStore().claim.customerNotes);
</script>
<template>
<div>
    <!-- Non-eligible modal confirmation -->
    <TransitionRoot as="template" :show="open && false">
      <Dialog as="div" class="relative z-10" @close="open = true">
        <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
          <div class="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" />
        </TransitionChild>

        <div class="fixed inset-0 z-10 overflow-y-auto">
          <div class="flex items-end justify-center min-h-full p-4 text-center sm:items-center sm:p-0">
            <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
              <DialogPanel class="relative px-4 pt-5 pb-4 overflow-hidden text-left transition-all transform bg-white rounded-lg shadow-xl sm:my-8 sm:w-full sm:max-w-sm sm:p-6">
                <div>
                  <div class="flex items-center justify-center w-12 h-12 mx-auto bg-green-100 rounded-full">
                    <CheckIcon class="w-6 h-6 text-green-600" aria-hidden="true" />
                  </div>
                  <div class="mt-3 text-center sm:mt-5">
                    <DialogTitle as="h3" class="text-base font-semibold leading-6 text-gray-900">Feedback envoyé</DialogTitle>
                    <div class="mt-2">
                      <p class="text-sm text-gray-500">Nous avons bien enregistré votre retour, nous n'engagerons pas de procédure pour ce dossier.</p>
                    </div>
                  </div>
                </div>
                <div class="mt-5 sm:mt-6">
                  <router-link to="/" class="inline-flex justify-center w-full px-3 py-2 text-sm font-semibold text-white bg-blue-600 rounded-md shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
                    @click="open = true">
                    Aller au dashboard
                  </router-link>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </TransitionRoot>
    <AlertModal type="warning"
      title="Abandonner le dossier"
      message="Vous avez indiqué que ce dossier n'était pas éligible à indemnisation. Voulez-vous continuer ?"
      confirm-message="Oui, je confirme" 
      :open=open @close="open = false" @open="open = true"
      @confirm="() => {
        open = false;
        saveAgentFeedback()
        openTerminationModal = true;
      }"
    />
    <AlertModal type="success"
      title="Merci de votre retour"
      message="Aucune procédure d'indemnisation ne sera enclenchée auprès des passagers.<br><br>
        <i>Si vous pensez avoir fait une erreur, il est de renvoyer à nouveau le formulaire.
          Sinon, vous pouvez fermer cette page.</i>"
      :open=openTerminationModal
      confirm-message="Retourner au dashboard"
      @confirm="() => {
        openTerminationModal = false;
        router.replace({ name: 'Home' })
      }"
      @close="() => {
        openTerminationModal = false;
        router.replace({ name: 'Home' })
      }"
    >
      
    </AlertModal>
  <form>
    <div class="space-y-12">    
      <div v-if="Boolean(customerNoteData)" class="pt-2 pb-6 border-b">
        <h2 class="inline-flex mb-5 text-base font-semibold leading-7 text-gray-900 align-center">
          <ChatBubbleBottomCenterTextIcon class="w-5 h-5 mt-1 mr-2 text-gray-700" />
          Notes Upclaim à destination de l'agence :
        </h2>
        <div class="mt-2">
          <blockquote class="px-6 mt-2 mb-3 text-base font-medium prose whitespace-pre-line gray-700">
            « <span class="italic">{{ customerNoteData }}</span> »
          </blockquote>
        </div>
      </div>

      <div class="pb-6 border-b border-gray-900/10">
        <div class="grid grid-cols-1">
          <div class="sm:block">
            <h2 class="inline-flex mb-5 text-base font-semibold leading-7 text-gray-900 align-center">
              <DocumentTextIcon class="w-5 h-5 mt-1 mr-2 text-gray-700"></DocumentTextIcon>
              Type de prestation
            </h2>
            <nav class="flex divide-x divide-gray-200 rounded-lg shadow isolate" aria-label="Tabs">
              <p v-for="(tab, tabIdx) in tabs" :key="tab.name"
                @click="selectedTab = tab"
                :class="[selectedTab.name === tab.name ? 'text-gray-900 bg-blue-50' : 'text-gray-500 hover:text-gray-700', tabIdx === 0 ? 'rounded-l-lg' : '', tabIdx === tabs.length - 1 ? 'rounded-r-lg' : '', 'group relative min-w-0 flex-1 overflow-hidden bg-white py-4 px-4 text-center text-sm font-medium hover:bg-gray-50 focus:z-10 cursor-pointer']"
                :aria-current="selectedTab.name === tab.name ? 'page' : undefined">
                <input :checked="selectedTab.name === tab.name" type="checkbox" class="w-4 h-4 mr-3 -ml-2 text-blue-600 border-gray-300 rounded focus:ring-blue-600" />
                <span>{{ tab.name }}</span>
                <span aria-hidden="true" :class="[selectedTab.name === tab.name ? 'bg-blue-600' : 'bg-transparent', 'absolute inset-x-0 bottom-0 h-0.5']" />
              </p>
            </nav>
          </div>
        </div>
        <div v-if="selectedTab.b2c === true" class="px-6 mt-10">
          <!-- <h2 class="text-sm font-semibold leading-7 text-gray-800">B.I. signé</h2>
          <p class="mt-1 text-sm leading-6 text-gray-600">Obligatoire sur du loisir (B2C).</p>
          <div class="my-4">
        
            <FileInput />

            <ul v-if="files.length" role="list" class="border border-gray-200 divide-y divide-gray-100 rounded-md">
              <li v-for="file in files" class="flex items-center justify-between py-4 pl-4 pr-5 text-sm leading-6">
                <div class="flex items-center flex-1 w-0">
                  <PaperClipIcon class="flex-shrink-0 w-5 h-5 text-gray-400" aria-hidden="true" />
                  <div class="flex flex-1 min-w-0 gap-2 ml-4">
                    <span class="font-medium truncate">{{ file.name }}</span>
                    <span class="flex-shrink-0 text-gray-400">{{ (file.size / 1024).toFixed(2) }} ko</span>
                  </div>
                </div>
                <div v-show="false" class="flex flex-shrink-0 ml-4 space-x-4">
                  <button type="button"
                    class="font-medium text-blue-600 bg-white rounded-md hover:text-blue-500">Update</button>
                  <span class="text-gray-200" aria-hidden="true">|</span>
                  <button type="button"
                    class="font-medium text-gray-900 bg-white rounded-md hover:text-gray-800">Remove</button>
                </div>
              </li>
            </ul>
          </div> -->
        </div>
        <div class="p-6 mt-4" v-else>
          <p class="text-sm font-medium leading-7 text-gray-800">Entreprise :</p>
          <SetTravelAgencyCompany
              :claim-id="useClaimStore().claim.id"
              :current-travel-agency-company="useClaimStore().claim?.legalSpecification.impactedSegment?.reservation.travelAgencyCompany"
          />
        </div>

      </div>
    </div>
    <div class="mt-10">

      <div class="col-span-full gap-x-6 gap-y-8 sm:grid-cols-6">

        <h2 class="inline-flex text-base font-semibold leading-7 text-gray-900">
          <ShieldCheckIcon class="w-5 h-5 mt-1 mr-2 text-gray-700" />
          Confirmer le dossier
        </h2>
        <p class="mt-1 text-sm leading-6 text-gray-600">Afin d'écarter un éventuel dysfonctionnement technique.</p>
        <RadioGroup v-model="selected" class="mt-8">
          <RadioGroupLabel class="sr-only">Confirmer dossier</RadioGroupLabel>
          <div class="-space-y-px bg-white rounded-md">
            <RadioGroupOption as="template" v-for="(setting, settingIdx) in settings" :key="setting.name"
              :value="setting" v-slot="{ checked, active }">
              <div
                :class="[settingIdx === 0 ? 'rounded-tl-md rounded-tr-md' : '', settingIdx === settings.length - 1 ? 'rounded-bl-md rounded-br-md' : '',
                  { 'z-10 border-green-200 bg-green-50': checked && settingIdx === 0 },
                  { 'z-10 border-red-200 bg-red-50': checked && settingIdx === 1 },
                  !checked && 'border-gray-200', 'relative flex cursor-pointer border p-4 focus:outline-none']"
                >
                <span
                  :class="[
                    checked
                      ? (
                        settingIdx === 0 ? 'bg-green-600 border-transparent' : 'bg-red-600 border-transparent'
                      )
                      : 'bg-white border-gray-300',
                    active ? (
                        settingIdx === 0 ? 'ring-2 ring-offset-2 ring-green-600' : 'ring-2 ring-offset-2 ring-red-600'
                     )
                     : '',
                    'mt-0.5 h-4 w-4 shrink-0 cursor-pointer rounded-full border flex items-center justify-center'
                  ]"
                  aria-hidden="true">
                  <span class="rounded-full bg-white w-1.5 h-1.5" />
                </span>
                <span class="flex flex-col ml-3">
                  <RadioGroupLabel as="span"
                    :class="[checked
                      ? (settingIdx === 0 ? 'text-green-900' : 'text-red-900' )
                      : 'text-gray-900', 'block text-sm font-medium'
                    ]">
                    {{ setting.name }}
                  </RadioGroupLabel>
                  <RadioGroupDescription as="span"
                    :class="[checked
                      ? (settingIdx === 0 ? 'text-green-700' : 'text-red-700' )
                      : 'text-gray-500', 'block text-sm'
                    ]">
                    {{ setting.description }}
                  </RadioGroupDescription>
                </span>
              </div>
            </RadioGroupOption>
          </div>
        </RadioGroup>
      </div>
    </div>

    <!-- <transition
      enter-active-class="transition duration-100 ease-out"
      enter-from-class="transform scale-95 opacity-0"
      enter-to-class="transform scale-100 opacity-100"
      leave-active-class="transition duration-75 ease-out"
      leave-from-class="transform scale-100 opacity-100"
      leave-to-class="transform scale-95 opacity-0"
    > -->
      <div class="py-10 mt-6" v-if="selectedTab.b2c && selected.eligible">
        <div>
          <ToggleWithDescription
            v-model="additionalLoss"
            label="Y a-t-il eu des préjudices supplémentaires ? (Hotel, ...)"
            description="Si sélectionné, vous serez emmenés sur un formulaire dédié pour nous les communiquer."
          />
          <div v-if="additionalLoss" class="pt-6">
            <Alert title="Le formulaire de l'étape suivante doit alors nous être envoyé"
              type="info" :with-border="false" />
          </div>
        </div>
      </div>
    <!-- </transition> -->


    <div class="mt-10">
      <label for="comment" class="block text-base font-medium leading-6 text-gray-900">Ajouter une note</label>
      <div class="mt-2">
        <textarea :rows="4" name="comment" id="comment" v-model="note"
          class="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6" />
      </div>
    </div>

    <div class="flex items-center justify-end mt-6 gap-x-6">
      <!-- <button type="button" class="text-sm font-semibold leading-6 text-gray-900">Cancel</button> -->
      <button type="submit"
        @click.prevent="submitForm"
        class="inline-flex px-3 py-2 text-base font-semibold text-white bg-blue-600 rounded-md shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600">
        <span v-if="isLoading" class="w-5 h-5 mr-3 -ml-1">
          <svg role="status" class="inline w-4 h-4 mx-2 text-blue-200 animate-spin"
            viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
              fill="currentColor" />
            <path
              d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
              fill="#1C64F2" />
          </svg>
        </span>
        Envoyer
      </button>
    </div>
  </form>
</div>
</template>